import { useCallback, useState } from 'react';

/**
 * React state hook backed by `window.localStorage`.
 *
 * - **Opt-in persistence**: when `key === null`, behaves exactly like
 *   `useState(initialValue)` — no read, no write. This lets callers
 *   conditionally enable persistence without violating React's rules
 *   of hooks.
 * - **Lazy init**: localStorage is read once at mount only.
 * - **Setter parity with useState**: accepts a value or a functional
 *   updater `(prev) => next`.
 * - **Resilient**: JSON parse errors, quota exceeded, and privacy-mode
 *   restrictions are caught silently — the in-memory value remains
 *   correct even when persistence fails.
 *
 * Introduced by PROD1.7.6 (#594) to back `FilterableTable.persistKey`
 * but kept generic for future UI preferences (theme, density, etc.).
 */
export function useLocalStorage<T>(
  key: string | null,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (!key) return initialValue;
    try {
      const raw = window.localStorage.getItem(key);
      return raw == null ? initialValue : (JSON.parse(raw) as T);
    } catch {
      return initialValue;
    }
  });

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        if (key) {
          try {
            window.localStorage.setItem(key, JSON.stringify(resolved));
          } catch {
            // localStorage unavailable (quota, privacy mode) — in-memory only
          }
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, update];
}
